import { useCallback, useEffect, useState } from 'react'
import { BookOpen, MessageSquareLock, Pencil, Plus, Power, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, Skeleton } from '@/components/States'
import { RelativeTime } from '@/components/DataDisplay'
import { useToast } from '@/providers/UiProviders'
import { fetchAllTickets, type AdminTicket } from '@/lib/adapters'
import {
  fetchTicketNotes,
  addTicketNote,
  fetchKbArticles,
  createKbArticle,
  updateKbArticle,
  deleteKbArticle,
  fetchAutoReplies,
  createAutoReply,
  toggleAutoReply,
  deleteAutoReply,
  type TicketNote,
  type KbArticle,
  type AutoReplyRule,
  TK_STR as S,
} from './ticketsApi'

/** A7.18 — Ichki muhokama. Tiket ichida agentlarning maxfiy izohlari (greenfield notes). */
export function InternalDiscussion() {
  const { toast } = useToast()
  const [tickets, setTickets] = useState<AdminTicket[]>([])
  const [ticketId, setTicketId] = useState('')
  const [notes, setNotes] = useState<TicketNote[] | null>(null)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    fetchAllTickets(ac.signal).then(setTickets).catch(() => setTickets([]))
    return () => ac.abort()
  }, [])

  useEffect(() => {
    if (!ticketId) {
      setNotes(null)
      return
    }
    const ac = new AbortController()
    setNotes(null)
    fetchTicketNotes(Number(ticketId), ac.signal).then(setNotes)
    return () => ac.abort()
  }, [ticketId, tick])

  const add = async () => {
    if (!ticketId || !body.trim()) return
    setBusy(true)
    try {
      await addTicketNote(Number(ticketId), body.trim())
      setBody('')
      toast(S.noteAdded, { tone: 'success' })
      setTick((t) => t + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.notes} screenId="A7.18" description={S.notesNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-2xl space-y-3">
          <select value={ticketId} onChange={(e) => setTicketId(e.target.value)} className="w-full rounded-md border border-border-default bg-surface-2 px-2.5 py-2 text-sm text-primary">
            <option value="">{S.pickTicket}</option>
            {tickets.map((t) => (
              <option key={t.id} value={t.id}>#{t.id} · {t.subject || '—'}</option>
            ))}
          </select>

          {ticketId && (
            <>
              <div className="space-y-2 rounded-lg border border-border-default bg-surface-2 p-3">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={S.notePlaceholder}
                  rows={3}
                  className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
                />
                <button onClick={add} disabled={busy || !body.trim()} className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50">
                  <Plus size={14} />
                  {S.add}
                </button>
              </div>

              {notes == null ? (
                <div className="space-y-1.5">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
              ) : notes.length === 0 ? (
                <div className="rounded-lg border border-border-default bg-surface-2 p-4 text-center text-sm text-tertiary">{S.greenfield}</div>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div key={n.id} className="rounded-lg border border-warning/40 bg-warning/5 p-3">
                      <div className="mb-1 flex items-center gap-2 text-xs text-tertiary">
                        <MessageSquareLock size={12} />
                        <span>{n.author ?? S.agent}</span>
                        <RelativeTime value={n.created_at} />
                      </div>
                      <div className="whitespace-pre-wrap text-sm text-primary">{n.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

const KB_CATEGORIES = ['general', 'billing', 'account', 'abuse', 'technical'] as const

/** A7.19 — Bilim bazasi. Agentlar va avto-javoblar uchun maqolalar (greenfield CRUD). */
export function KnowledgeBase() {
  const { toast } = useToast()
  const [rows, setRows] = useState<KbArticle[] | null>(null)
  const [editId, setEditId] = useState<number | null>(null) // null=none, 0=new
  const [fTitle, setFTitle] = useState('')
  const [fCat, setFCat] = useState<string>('general')
  const [fBody, setFBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    fetchKbArticles(ac.signal).then(setRows)
    return () => ac.abort()
  }, [tick])

  const openNew = () => {
    setEditId(0)
    setFTitle('')
    setFCat('general')
    setFBody('')
  }
  const openEdit = (a: KbArticle) => {
    setEditId(a.id)
    setFTitle(a.title)
    setFCat(a.category)
    setFBody(a.body)
  }
  const save = async () => {
    if (!fTitle.trim() || !fBody.trim()) return
    setBusy(true)
    try {
      const b = { title: fTitle.trim(), category: fCat, body: fBody.trim() }
      if (editId && editId > 0) await updateKbArticle(editId, b)
      else await createKbArticle(b)
      setEditId(null)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteKbArticle(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={S.kb}
        screenId="A7.19"
        description={S.kbNote}
        actions={
          <button onClick={openNew} className="flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
            <Plus size={14} />
            {S.create}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-3">
          {editId !== null && (
            <div className="space-y-2 rounded-lg border border-accent bg-surface-2 p-3">
              <div className="flex gap-2">
                <input value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder={S.kbTitle} className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
                <select value={fCat} onChange={(e) => setFCat(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
                  {KB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <textarea value={fBody} onChange={(e) => setFBody(e.target.value)} placeholder={S.kbBody} rows={5} className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <div className="flex gap-2">
                <button onClick={save} disabled={busy || !fTitle.trim() || !fBody.trim()} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">{S.save}</button>
                <button onClick={() => setEditId(null)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">{S.cancel}</button>
              </div>
            </div>
          )}

          {!rows ? (
            <div className="space-y-1.5">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.greenfield} icon={<BookOpen size={28} strokeWidth={1.5} />} />
          ) : (
            <div className="space-y-2">
              {rows.map((a) => (
                <div key={a.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary">{a.title}</span>
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">{a.category}</span>
                        <span className="text-xs text-tertiary">{a.views} {S.views}</span>
                      </div>
                      <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-secondary">{a.body}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button onClick={() => openEdit(a)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-primary"><Pencil size={15} /></button>
                      <button onClick={() => del(a.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const MATCH_TYPES = ['contains', 'equals', 'regex'] as const

/** A7.20 — Avtomatik javoblar. Triggerga qarab avto-javob qoidalari (greenfield). */
export function AutoReplies() {
  const { toast } = useToast()
  const [rows, setRows] = useState<AutoReplyRule[] | null>(null)
  const [open, setOpen] = useState(false)
  const [fTrigger, setFTrigger] = useState('')
  const [fMatch, setFMatch] = useState<string>('contains')
  const [fResp, setFResp] = useState('')
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    fetchAutoReplies(ac.signal).then(setRows)
    return () => ac.abort()
  }, [tick])

  const create = async () => {
    if (!fTrigger.trim() || !fResp.trim()) return
    setBusy(true)
    try {
      await createAutoReply({ trigger: fTrigger.trim(), match_type: fMatch, response: fResp.trim() })
      setOpen(false)
      setFTrigger('')
      setFResp('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const toggle = async (r: AutoReplyRule) => {
    try {
      await toggleAutoReply(r.id, !r.enabled)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteAutoReply(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const matchLabel = (m: string) => (S as Record<string, string>)[m] ?? m

  return (
    <>
      <PageHeader
        title={S.auto}
        screenId="A7.20"
        description={S.autoNote}
        actions={
          <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
            <Plus size={14} />
            {S.create}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-3">
          {open && (
            <div className="space-y-2 rounded-lg border border-accent bg-surface-2 p-3">
              <div className="flex gap-2">
                <input value={fTrigger} onChange={(e) => setFTrigger(e.target.value)} placeholder={S.trigger} className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
                <select value={fMatch} onChange={(e) => setFMatch(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
                  {MATCH_TYPES.map((m) => <option key={m} value={m}>{matchLabel(m)}</option>)}
                </select>
              </div>
              <textarea value={fResp} onChange={(e) => setFResp(e.target.value)} placeholder={S.response} rows={3} className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <div className="flex gap-2">
                <button onClick={create} disabled={busy || !fTrigger.trim() || !fResp.trim()} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">{S.add}</button>
                <button onClick={() => setOpen(false)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">{S.cancel}</button>
              </div>
            </div>
          )}

          {!rows ? (
            <div className="space-y-1.5">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.greenfield} icon={<Power size={28} strokeWidth={1.5} />} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">{matchLabel(r.match_type)}</span>
                        <span className="text-sm font-medium text-primary">{r.trigger}</span>
                        <span className={`text-xs ${r.enabled ? 'text-success' : 'text-tertiary'}`}>{r.enabled ? S.enabled : S.disabled}</span>
                        {r.hits > 0 && <span className="text-xs text-tertiary">{r.hits} {S.hits}</span>}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-secondary">{r.response}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button onClick={() => toggle(r)} className={`rounded-md p-1.5 hover:bg-surface-3 ${r.enabled ? 'text-success' : 'text-tertiary'}`} title={r.enabled ? S.disabled : S.enabled}><Power size={15} /></button>
                      <button onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
